import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import type { Anime, NewsItem } from "@/types/content";

export const PAGE_SIZE = 8;
export const NEWS_PAGE_SIZE = 5;

export function getAnimeById(id: string | undefined): Anime | undefined {
  if (!id) return undefined;
  return animeList.find((item) => String(item.id) === id) as Anime | undefined;
}

export function getNewsById(id: string | undefined): NewsItem | undefined {
  if (!id) return undefined;
  return newsList.find((item) => String(item.id) === id) as NewsItem | undefined;
}

export function formatNumber(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)} 万`;
  }
  return String(value);
}

export interface AnimeFilterOptions {
  tags: string[];
  regions: string[];
  years: number[];
  sort: string;
}

export type SortKey = "update" | "popular" | "rating";

export function filterAnime(
  source: Anime[],
  options: Partial<AnimeFilterOptions> = {}
): Anime[] {
  const { tags = [], regions = [], years = [], sort = "update" } = options;
  const result = source.filter((item) => {
    const tagMatch = tags.length === 0 || item.tags.some((tag) => tags.includes(tag));
    const regionMatch = regions.length === 0 || regions.includes(item.region);
    const yearMatch = years.length === 0 || years.includes(item.year);
    return tagMatch && regionMatch && yearMatch;
  });

  const sorted = [...result];
  if (sort === "popular") {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else if (sort === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else {
    sorted.sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
  }
  return sorted;
}

export function getRelatedAnime(current: Anime, count = 3): Anime[] {
  return animeList
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const tagScore = item.tags.filter((tag) => current.tags.includes(tag)).length;
      return { item, score: tagScore * 100 + item.rating };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item as Anime);
}

export function getRelatedNews(current: NewsItem, count = 3): NewsItem[] {
  return newsList
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const tagScore = item.tags.filter((tag) => current.tags.includes(tag)).length;
      return { item, score: tagScore * 100 + (item.category === current.category ? 20 : 0) };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item as NewsItem);
}

export function getNewsForAnime(anime: Anime, count = 3): NewsItem[] {
  return newsList
    .map((item) => {
      let score = 0;
      if (item.tags.includes(anime.title)) score += 200;
      if (item.tags.some((tag) => anime.tags.includes(tag))) score += 50;
      if (item.title.includes(anime.title)) score += 150;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || (a.item.date < b.item.date ? 1 : -1))
    .slice(0, count)
    .map((entry) => entry.item as NewsItem);
}

export function searchAnime(keyword: string): Anime[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return animeList.filter((item) => {
    const haystack = `${item.title} ${item.tags.join(" ")} ${item.region} ${item.summary} ${item.description}`.toLowerCase();
    return haystack.includes(key);
  });
}

export function searchNews(keyword: string): NewsItem[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return newsList.filter((item) => {
    const haystack = `${item.title} ${item.excerpt} ${item.content.join(" ")} ${item.tags.join(" ")} ${item.category}`.toLowerCase();
    return haystack.includes(key);
  });
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(length: number, pageSize: number): number {
  return Math.max(1, Math.ceil(length / pageSize));
}